import React, { useState, useEffect } from "react"
import { Card } from "ui/Card"
import { Form, Input, Button, Flex, Select } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Icon } from "ui/Icon"
import PropTypes from "prop-types"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { colors } from "themes/variables"
import { SelectTag } from "ui/SelectTag"
import { modelSelector } from "store/selectors"
import { getTripSubcategories, seedTripSubcategories } from "store/trips"

const { TextArea } = Input

export const ExperienceInfo = ({ form, onDescriptionChange }) => {
  const [showAiModal, setShowAiModal] = useState(false)
  const [descriptionValue, setDescriptionValue] = useState("")
  const [profileDescription, setProfileDescription] = useState("")
  const [t] = useTranslation()
  const dispatch = useDispatch()

  const { data: tripCategories, loading: categoriesLoading } = useSelector((state) => state.trips.tripCategories)
  const { data: tripSubcategories } = useSelector((state) => state.trips.tripSubcategories)
  const trip = useSelector(modelSelector("trip"))
  const selectedCategory = Form.useWatch("categories", form)

  // On edit load: fetch subcategories for the trip's existing category so the Select has options
  useEffect(() => {
    if (trip?.category_id) {
      dispatch(getTripSubcategories(trip.category_id))
    }
  }, [trip?.id, trip?.category_id, dispatch])

  const categoryOptions = tripCategories.map((cat) => ({ value: cat.id, label: cat.name }))
  const subcategoryOptions = tripSubcategories.map((tag) => ({ value: tag.id, label: tag.name }))

  const handleCategoryChange = (value) => {
    form.setFieldsValue({ categories: value, sub_categories: undefined })
    if (value) dispatch(getTripSubcategories(value))
    else dispatch(seedTripSubcategories([]))
  }

  useEffect(() => {
    form.setFieldsValue({ description: descriptionValue })
    onDescriptionChange?.()
  }, [descriptionValue, form])

  useEffect(() => {
    setDescriptionValue(form.getFieldValue("description") || "")
  }, [form.getFieldValue("description")])

  return (
    <>
      <Card title={t("experiences.details.experience_info")} className="mb-xl">
        <Form.Item
          name="categories"
          label={t("experiences.details.labels.category")}
          rules={[{ required: true, message: t("experiences.details.errors.category_required", "Please select a category") }]}
        >
          <Select
            placeholder={t("experiences.details.select_category_placeholder")}
            style={{ width: "100%" }}
            options={categoryOptions}
            onChange={handleCategoryChange}
            loading={categoriesLoading}
          />
        </Form.Item>

        <Form.Item
          name="sub_categories"
          label={t("experiences.details.labels.marketplace_tags")}
          rules={[{ required: true, message: t("experiences.details.errors.marketplace_tags_required") }]}
        >
          <Select
            mode="multiple"
            showSearch
            placeholder={
              selectedCategory
                ? t("experiences.details.placeholders.select_tags")
                : t("experiences.details.placeholders.select_category_first", "Select a category first")
            }
            options={subcategoryOptions}
            disabled={!selectedCategory}
            optionFilterProp="label"
            tagRender={SelectTag}
          />
        </Form.Item>

        <Form.Item
          name="title"
          label={t("experiences.details.labels.listing_title")}
          rules={[{ required: true, message: t("experiences.details.listing_title_error") }]}
        >
          <Input placeholder={t("experiences.details.listing_title_placeholder")} />
        </Form.Item>

        <Flex justify="flex-end" className="mb-s">
          <Button
            type="primary"
            icon={
              <Icon
                name="aiEnhancement"
                color={descriptionValue.length < 25 ? colors.textPrimary : colors.disabled}
                height={20}
                width={20}
              />
            }
            onClick={() => setShowAiModal(true)}
            disabled={descriptionValue.length < 25}
          >
            {t("client.enhance_with_ui")}
          </Button>
        </Flex>

        <Form.Item
          name="description"
          label={t("global.description")}
          rules={[{ required: true, message: t("pro.ai_enhancement.description_too_short") }]}
        >
          <TextArea
            placeholder={t("experiences.details.description_placeholder")}
            rows={4}
            onChange={(e) => setDescriptionValue(e.target.value)}
            value={descriptionValue}
          />
        </Form.Item>
      </Card>

      {showAiModal && (
        <AiEnhancementModal
          open={showAiModal}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          profileDescription={profileDescription}
          setProfileDescription={setProfileDescription}
          onOk={() => setShowAiModal(false)}
          onCancel={() => {
            setShowAiModal(false)
            setProfileDescription("")
          }}
          text={t("client.experience_content")}
          modalTitle={t("client.experiences_enhancer")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("client.refinement_tooltip")}
          improveWithAITooltipTitle={t("client.improve_with_ai_tooltip_for_experiences")}
        />
      )}
    </>
  )
}

ExperienceInfo.propTypes = {
  form: PropTypes.object,
  onDescriptionChange: PropTypes.func
}
